import { CallWritebackStatus } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/writeback/v1/writeback_service.pb';
import { PetDetails } from '@weave/schema-gen-ts/dist/schemas/pets-api/v1/pets_api.pb';
import dayjs from 'dayjs';
import { CallIntelligenceWritebackV1 } from '@frontend/api-call-intel';
import { i18next } from '@frontend/i18n';
import type { IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { ChipVariants } from '@frontend/design-system';

interface BaseSummarySyncChipConfig {
  backgroundColor: string;
  borderColor: string;
  color?: string;
  iconName: IconName | 'loader';
  chipVariant: ChipVariants;
  defaultLabel?: string;
  disabled?: boolean;
  isLoading?: boolean;
}

type SyncStatusType = 'success' | 'loading' | 'disabled' | 'failed' | 'sync-ready' | 'default';

const SummarySyncStatusConfigMap: Record<SyncStatusType, BaseSummarySyncChipConfig> = {
  success: {
    backgroundColor: theme.colors.success5,
    borderColor: theme.colors.success70,
    color: theme.colors.success70,
    iconName: 'success-filled-small',
    chipVariant: 'success',
    defaultLabel: i18next.t('Synced', { ns: 'call-intelligence' }),
  },
  loading: {
    backgroundColor: 'transparent',
    borderColor: theme.colors.neutral50,
    iconName: 'loader',
    chipVariant: 'neutral',
    defaultLabel: i18next.t('Syncing...', { ns: 'call-intelligence' }),
  },
  disabled: {
    backgroundColor: theme.colors.neutral5,
    borderColor: 'transparent',
    iconName: 'data-sync-small',
    chipVariant: 'disabled',
    disabled: true,
  },
  failed: {
    backgroundColor: theme.colors.critical5,
    borderColor: theme.colors.critical70,
    color: theme.colors.critical70,
    iconName: 'error-filled-small',
    chipVariant: 'critical',
    defaultLabel: i18next.t('Failed', { ns: 'call-intelligence' }),
  },
  'sync-ready': {
    backgroundColor: 'transparent',
    borderColor: theme.colors.neutral50,
    color: theme.colors.neutral70,
    iconName: 'data-sync-small',
    chipVariant: 'default',
    defaultLabel: i18next.t('Sync Ready', { ns: 'call-intelligence' }),
  },
  default: {
    backgroundColor: 'transparent',
    borderColor: theme.colors.neutral50,
    color: theme.colors.neutral70,
    iconName: 'data-sync-small',
    chipVariant: 'default',
  },
};

export interface ExtendedSummarySyncChipConfig extends BaseSummarySyncChipConfig {
  tooltip?: string;
  label: string;
}

type GetSummarySyncChipConfigParams = {
  isSyncing?: boolean;
  isContactMissing?: boolean;
  hasManualSyncAccess?: boolean;
} & (
  | {
      writebackStatusList: CallWritebackStatus[];
      writebackStatus?: never;
    }
  | {
      writebackStatus: CallWritebackStatus;
      writebackStatusList?: never;
    }
);

export const getSummarySyncChipConfig = ({
  writebackStatus,
  writebackStatusList,
  isSyncing,
  isContactMissing,
  hasManualSyncAccess,
}: GetSummarySyncChipConfigParams) => {
  const aggregatedSyncStats = getAggregatedSyncStats(writebackStatusList ?? [writebackStatus]);
  const isDisabled = isContactMissing || !hasManualSyncAccess;

  const label = getSummarySyncLabel({
    ...aggregatedSyncStats,
    writebackStatus: aggregatedSyncStats.aggregatedStatus,
    isSyncing,
  });

  let baseConfig: BaseSummarySyncChipConfig = SummarySyncStatusConfigMap.default;
  let tooltip = '';

  switch (aggregatedSyncStats.aggregatedStatus) {
    case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_SUCCEEDED:
      baseConfig = SummarySyncStatusConfigMap.success;
      break;
    case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_FAILED:
      baseConfig = SummarySyncStatusConfigMap.failed;
      break;
    case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_READY_TO_SYNC:
      baseConfig = isDisabled ? SummarySyncStatusConfigMap.disabled : SummarySyncStatusConfigMap['sync-ready'];
      tooltip = isContactMissing
        ? i18next.t('Create a patient record in your PMS or EHR to sync the summary', {
            ns: 'call-intelligence',
          })
        : !hasManualSyncAccess
        ? i18next.t('You do not have permission to manually sync', { ns: 'call-intelligence' })
        : '';
      break;
    case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_TRIGGERED:
    case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_INPROGRESS:
      baseConfig = { ...SummarySyncStatusConfigMap.loading, isLoading: true };
      break;
    default:
      baseConfig = isDisabled ? SummarySyncStatusConfigMap.disabled : SummarySyncStatusConfigMap.default;
  }

  return {
    ...baseConfig,
    tooltip,
    label,
    aggregatedWritebackStatus: aggregatedSyncStats.aggregatedStatus,
  };
};

export const getSummarySyncLabel = ({
  writebackStatus,
  failedStatusCount,
  succeededStatusCount,
  isMulti,
  isSyncing,
}: {
  writebackStatus: CallIntelligenceWritebackV1.Types.WritebackStatus;
  failedStatusCount?: number;
  succeededStatusCount?: number;
  isMulti?: boolean;
  isSyncing?: boolean;
}): string => {
  if (isSyncing) {
    return i18next.t('Syncing...', { ns: 'call-intelligence' });
  }

  switch (writebackStatus) {
    case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_SUCCEEDED: {
      return isMulti
        ? i18next.t('{{count}} Synced', { count: succeededStatusCount, ns: 'call-intelligence' })
        : i18next.t('Synced', { ns: 'call-intelligence' });
    }
    case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_FAILED:
      return isMulti
        ? i18next.t('{{count}} Failed', { count: failedStatusCount, ns: 'call-intelligence' })
        : i18next.t('Failed Sync', { ns: 'call-intelligence' });

    case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_READY_TO_SYNC:
      return i18next.t('Sync Summary', { ns: 'call-intelligence' });

    case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_TRIGGERED:
    case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_INPROGRESS:
      return i18next.t('Syncing...', { ns: 'call-intelligence' });
    default:
      return i18next.t('Sync Summary', { ns: 'call-intelligence' });
  }
};

export const getAggregatedSyncStats = (writebackStatusList: CallWritebackStatus[]) => {
  const totalStatuses = writebackStatusList?.length || 0;
  const isMulti = totalStatuses > 1;

  const failedStatusCount =
    writebackStatusList?.filter(
      ({ status }) => status === CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_FAILED
    ).length ?? 0;

  const succeededStatusCount =
    writebackStatusList?.filter(
      ({ status }) => status === CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_SUCCEEDED
    ).length ?? 0;

  const isInProgressStatus = writebackStatusList?.some(
    ({ status }) =>
      status === CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_TRIGGERED ||
      status === CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_INPROGRESS
  );

  const hasReadyToSyncStatus = writebackStatusList?.some(
    ({ status }) => status === CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_READY_TO_SYNC
  );

  const aggregatedStatus = isInProgressStatus
    ? CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_INPROGRESS
    : failedStatusCount
    ? CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_FAILED
    : succeededStatusCount
    ? CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_SUCCEEDED
    : hasReadyToSyncStatus
    ? CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_READY_TO_SYNC
    : CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_UNKOWN;

  return {
    totalStatuses,
    isMulti,
    failedStatusCount,
    succeededStatusCount,
    aggregatedStatus,
  };
};

const SYNC_DATE_FORMAT = 'MMM DD, YYYY hh:mm a';
const SYNC_TIME_FORMAT = 'hh:mm a';

export const formatSyncDate = (date: Date | string): string => {
  const dateObject = dayjs(typeof date === 'string' ? new Date(date) : date);
  const isSameDay = dayjs().isSame(dateObject, 'day');
  return dateObject.format(isSameDay ? SYNC_TIME_FORMAT : SYNC_DATE_FORMAT);
};

export const getSyncErrorMessage = ({
  summarySyncMode,
  writebackStatusResponse,
}: {
  writebackStatusResponse?: CallWritebackStatus;
  summarySyncMode?: CallIntelligenceWritebackV1.Types.WritebackSettingMode;
}): string => {
  if (writebackStatusResponse?.status !== CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_FAILED) {
    return '';
  }

  const prefixMessage =
    summarySyncMode === CallIntelligenceWritebackV1.Types.WritebackSettingMode.WRITEBACK_SETTING_MODE_MANUAL
      ? i18next.t('Manual sync failed.', { ns: 'call-intelligence' })
      : i18next.t('Automatic sync failed.', { ns: 'call-intelligence' });

  const pmsErrorMessage = writebackStatusResponse?.errorMessage
    ? writebackStatusResponse.errorMessage.replace(/\.?$/, '.') // Add full stop at the end if not present
    : '';

  const nextRetryMessage =
    summarySyncMode === CallIntelligenceWritebackV1.Types.WritebackSettingMode.WRITEBACK_SETTING_MODE_AUTOMATIC &&
    !!writebackStatusResponse?.nextRetryAt
      ? i18next.t('Next auto-retry at {{time}}.', {
          time: formatSyncDate(writebackStatusResponse.nextRetryAt),
          ns: 'call-intelligence',
        })
      : '';

  return [prefixMessage, pmsErrorMessage, nextRetryMessage].filter(Boolean).join(' ').trim();
};

export const getPetPmsServiceableId = (pet: PetDetails) => {
  return pet.petPmDisplayId || pet.petPmsId;
};
